import { Injectable, signal } from '@angular/core';

/**
 * Audio service for split-flap character flip sounds.
 *
 * Plays a mechanical "clack" sound on each character flip with:
 * - Volume normalization based on number of simultaneous sounds
 * - Global mute toggle with localStorage persistence
 * - Web Audio API for low-latency playback
 *
 * Sound source priority:
 * 1. /public/assets/sounds/flip.mp3 (if present)
 * 2. Synthesized mechanical click (fallback, always works)
 */
@Injectable({
  providedIn: 'root',
})
export class SplitFlapAudioService {
  private audioContext: AudioContext | null = null;
  private audioBuffer: AudioBuffer | null = null;
  private useSynthesized = false;
  private activeSounds = 0;

  // Mute state (persisted in localStorage)
  private muteSignal = signal(this.loadMutePreference());
  readonly isMuted = this.muteSignal.asReadonly();

  private readonly SOUND_PATH = '/assets/sounds/flip.mp3';

  // Base volume (0.0 to 1.0)
  private readonly BASE_VOLUME = 0.3;

  private tabHidden = false;

  constructor() {
    this.initAudioContext();
    this.listenForVisibilityChange();
  }

  /**
   * Initialize Web Audio API context and attempt to load sound file.
   * Falls back to synthesized sound if file is missing or fails to load.
   */
  private async initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Web Audio API not available:', error);
      return;
    }

    try {
      const response = await fetch(this.SOUND_PATH);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      console.info('SplitFlapAudio: using sound file');
    } catch {
      this.useSynthesized = true;
      console.info('SplitFlapAudio: flip.mp3 not found, using synthesized sound');
    }
  }

  /**
   * Play flip sound with volume normalization.
   * Uses loaded audio file if available, otherwise synthesizes the click.
   */
  playFlip() {
    if (this.muteSignal() || this.tabHidden || !this.audioContext) return;
    if (!this.audioBuffer && !this.useSynthesized) return; // still loading

    this.activeSounds++;
    const normalizedVolume = this.BASE_VOLUME / Math.sqrt(this.activeSounds);
    const onEnded = () => {
      this.activeSounds = Math.max(0, this.activeSounds - 1);
    };

    try {
      if (this.audioBuffer) {
        this.playFromBuffer(normalizedVolume, onEnded);
      } else {
        this.playSynthesized(normalizedVolume, onEnded);
      }
    } catch (error) {
      console.error('Error playing flip sound:', error);
      this.activeSounds = Math.max(0, this.activeSounds - 1);
    }
  }

  /**
   * Play the loaded audio buffer.
   */
  private playFromBuffer(volume: number, onEnded: () => void) {
    const ctx = this.audioContext!;
    const source = ctx.createBufferSource();
    source.buffer = this.audioBuffer;

    const gainNode = ctx.createGain();
    gainNode.gain.value = volume;

    source.connect(gainNode);
    gainNode.connect(ctx.destination);
    source.onended = onEnded;
    source.start(0);
  }

  /**
   * Synthesize a mechanical click sound using Web Audio API primitives.
   *
   * Modeled after a real split-flap clack:
   * - Short burst of bandpass-filtered white noise (the "body" of the click)
   * - Sharp attack, fast exponential decay (~40ms total)
   * - Bandpass centered around 1.2kHz for a crisp mechanical tone
   */
  private playSynthesized(volume: number, onEnded: () => void) {
    const ctx = this.audioContext!;
    const now = ctx.currentTime;
    const duration = 0.045; // 45ms

    // White noise buffer
    const bufferSize = Math.ceil(ctx.sampleRate * duration);
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = ctx.createBufferSource();
    source.buffer = noiseBuffer;

    // Bandpass filter — shapes noise into a crisp mechanical click
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1200;
    filter.Q.value = 0.8;

    // Gain envelope: instant attack, fast exponential decay
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + 0.002);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(ctx.destination);
    source.onended = onEnded;
    source.start(now);
  }

  /**
   * Toggle mute state and persist to localStorage.
   */
  toggleMute() {
    const newMuteState = !this.muteSignal();
    this.muteSignal.set(newMuteState);
    this.saveMutePreference(newMuteState);
    if (!newMuteState) {
      this.resumeAudioContext();
    }
  }

  /**
   * Set mute state directly.
   */
  setMute(muted: boolean) {
    this.muteSignal.set(muted);
    this.saveMutePreference(muted);
  }

  /**
   * Resume audio context if suspended (required after user interaction in some browsers).
   */
  async resumeAudioContext() {
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  private listenForVisibilityChange() {
    document.addEventListener('visibilitychange', () => {
      this.tabHidden = document.hidden;
      if (!this.audioContext) return;

      if (document.hidden) {
        this.audioContext.suspend();
      } else if (!this.muteSignal()) {
        this.audioContext.resume();
      }
    });
  }

  private loadMutePreference(): boolean {
    return localStorage.getItem('flippy_audio_muted') === 'true';
  }

  private saveMutePreference(muted: boolean) {
    localStorage.setItem('flippy_audio_muted', muted.toString());
  }
}
